import type { PersonId, TreeToTreeMatch } from '@fs/zion-tree-types'
import Cache from '@fs/zion-cache'
import { useSwrRequest } from './useSwrRequest'

type Tree2TreeMatchesResponse = {
  tdMatches: TreeToTreeMatch[]
}

type ProcessedTree2TreeMatches = {
  data: TreeToTreeMatch[]
  isLoading: boolean
  error: boolean
}

// Endpoint URL for tree-to-tree matches
const swrKey = (personId: PersonId): string =>
  `/service/tree/tree-data/match/persons/${personId}?matchTypes=OTHER_TREE&includeParentHints=true&includeSpouseHints=true`

const hintsCache = new Cache({ storeName: 'tree2treeHints_r9' })

/**
 * Hook to fetch tree-to-tree matches for a person.
 * Applies endpoint safeguards because we don't trust tree-data, and may need to use the hook for more parts of the UI.
 * @param options Object containing personId, treeId, and type parameters
 * @param options.personId The ID of the person to retrieve potential matches for
 * @param options.treeId The ID of the current tree to filter out matches from the same tree
 * @param options.type The type of matches to retrieve (PERSON or PARENTS)
 * @returns ProcessedTree2TreeMatches with the filtered and sorted matches data and helper properties
 */
export function useTree2TreeMatches({
  personId,
  treeId,
  type,
}: {
  personId?: PersonId
  treeId: string
  type: 'PERSON' | 'PARENTS'
}): ProcessedTree2TreeMatches {
  const url = personId ? swrKey(personId) : null

  const { data, error, isLoading } = useSwrRequest<Tree2TreeMatchesResponse>(url, hintsCache, {
    dedupingInterval: 10000, // Dedupe fetching the hints by 10 seconds
    focusThrottleInterval: 60 * 1000 * 2, // Only refetch the hints when the tab gets refocused if more than 2 minutes has passed since last refetch
  })

  const dataToSort = data?.tdMatches || [] // mockTree2TreeHintResults(treeId) // Mock data for development purposes

  // Apply endpoint safeguards:
  // 1. Filter out results from the same tree.
  // 2. Filter out results below our target confidence level.
  // (3). If in parent mode, filter out results without potential parents.
  // 4. Sort results descending by confidence level.
  // 5. Limit results to a maximum of 3 entries.
  let filteredAndSortedData = dataToSort
    .filter((entry) => entry.person?.treeId && entry.person?.treeId !== treeId)
    .filter((entry) => entry.confidence >= 4)

  if (type === 'PARENTS') {
    filteredAndSortedData = filteredAndSortedData.filter((entry) => entry.potentialParents)
  }

  filteredAndSortedData = [...filteredAndSortedData].sort((a, b) => b.confidence - a.confidence).slice(0, 3)

  return {
    data: filteredAndSortedData,
    isLoading,
    error: !!error,
  }
}
